import { refs, domReady, booleanDataset, dispatchEvent } from '../modules/utils.js';
import { test as testMedia } from '../modules/match-media.js';

class ArchiveFeed extends HTMLElement {
  #columnsSizes = [0, 0, 0, 0];
  #isLoading = false;
  #hasNextPage = false; // blocks int observer at startup
  #items = [];

  static get observedAttributes() {
    return ['data-page', 'data-tags'];
  }

  get minColumnSize() {
    if (testMedia('small')) {
      // test only the first and the second columns
      return this.#columnsSizes[1] < this.#columnsSizes[0] ? 1 : 0;
    }
    return this.#columnsSizes.indexOf(Math.min(...this.#columnsSizes));
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-page') {
      if (newValue) {
        if (this.#isLoading) {
          this.addEventListener('loaded', () => this.#update(), { once: true });
        }
        else {
          this.#update();
        }
      }
    }

    if (name === 'data-tags') {
      // clear columns
      this.#resetColumns();
      const tags = newValue ? newValue.split(',') : [];
      if (tags.length) {
        this.refs.tags?.forEach((el) => booleanDataset(el, 'active', tags.includes(el.textContent)));
      } else {
        this.refs.tags?.forEach((el, i) => booleanDataset(el, 'active', i === 0));
      }
    }
  }

  async connectedCallback() {
    await domReady();
    this.refs = refs(this); // Use private field
    this.dataset.tags = '';
    this.dataset.page = 1;
    this.#listeners();
  }

  disconnectedCallback() {
    // Cleanup when element is removed
    this.#isLoading = false;
    this.#columnsSizes = [0, 0, 0, 0];
    this.refs = null;
  }

  #listeners() {
    this.refs.tags?.forEach((el, i) => {
      el.addEventListener('click', () => {
        if (this.#isLoading) return;

        let value = el.textContent;
        if (i === 0) {
          // all
          this.dataset.tags = '';
        }
        else {
          let tags = this.dataset.tags ? this.dataset.tags.split(',') : [];
          if (tags.includes(value)) {
            this.dataset.tags = tags.filter((tag) => tag !== value).join(',');
          } else {
            this.dataset.tags = [...tags, value].join(',');
          }
        }
        this.dataset.page = 1;
      });
    });

    this.refs.toggle?.addEventListener('click', () => this.#toggleFilters());

    // next page on scroll
    const intObserver = new IntersectionObserver((entries) => {
      if (!this.#hasNextPage) return;
      if (entries[0].isIntersecting) {
        if (this.#isLoading) {
          this.addEventListener('loaded', () => {
            this.dataset.page = parseInt(this.dataset.page) + 1;
          }, { once: true });
        } else {
          this.dataset.page = parseInt(this.dataset.page) + 1;
        }
      }
    });

    intObserver.observe(this.refs.loader);
  }

  async #update() {
    const items = await this.#loadItems();
    await this.#renderItems(items);
    return true;
  }

  #resetColumns() {
    this.#columnsSizes = [0, 0, 0, 0];
    this.refs.columns.forEach((el) => el.innerHTML = '');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  async #loadItems() {
    this.#isLoading = true;
    const page = this.dataset.page;
    const tags = this.dataset.tags ? btoa(this.dataset.tags) : '';
    const url = `${this.dataset.fetchUrl}/${page}/${tags}`;
    return fetch(url, {
      method: 'GET',
      headers: { 'Authorization': 'Basic YXBpQGx1Z2xpbzcuY29tOmFwaXBhc3N3b3Jk' }
    })
      .then((res) => res.json())
      .then((data) => {
        this.#isLoading = false;
        if (data.error) {
          return [];
        }

        if (data.page === 1) {
          this.#items = [];
        }
        this.#items = [...this.#items, ...data.items];
        // console.log(this.#items);

        this.#hasNextPage = data.hasNextPage;
        this.dispatchEvent(new CustomEvent('loaded'));
        return data.items;
      });
  }

  async #renderItems(items) {
    if (!items?.length) return;

    items.forEach((item) => {
      const template = this.refs.itemTemplate.content.cloneNode(true);
      const itemEl = template.querySelector('[data-item-ref]');
      const videoEl = template.querySelector('[data-video-ref]');
      const imageEl = template.querySelector('[data-image-ref]');

      if (item.type === 'image') {
        videoEl.remove();
        imageEl.src = item.imagePreviewUrl;
        imageEl.alt = item.name;
      }
      if (item.type === 'video') {
        imageEl.remove();
        videoEl.src = item.videoPreviewUrl;
        videoEl.alt = item.name;
      }
      itemEl.dataset.size = item.size;

      const index = this.minColumnSize;
      this.refs.columns[index].append(template);
      const sizeHeightValues = [
        null,
        0.66666666666666666667,
        0.86956521739130434783,
        1,
        1.5
      ];
      this.#columnsSizes[index] += sizeHeightValues[item.size];

      itemEl.addEventListener('click', () => {
        if (item.type === 'video' && testMedia('small')) {
          if (videoEl.requestFullscreen) {
            videoEl.requestFullscreen();
          } else if (videoEl.webkitEnterFullscreen) { // Safari
            videoEl.addEventListener('webkitendfullscreen', () => {
              videoEl.src = item.videoPreviewUrl;
            }, { once: true });
            videoEl.webkitEnterFullscreen();
            setTimeout(() => {
              videoEl.src = item.videoUrl;
            }, 10);
          }
        } else {
          dispatchEvent('archivefeed:gallery', {
            index: item.index,
            items: this.#getItemsById(item.id)
          });
        }
      });

      setTimeout(() => {
        booleanDataset(itemEl, 'display', true);
      }, 100);
    });
  }

  #getItemsById(id) {
    const items = this.#items.filter((item) => item.id === id);
    items.sort((a, b) => a.index - b.index);
    return items;
  }

  #toggleFilters() {
    const listEl = this.refs.toggle.parentElement;
    const value = listEl.dataset.hasOwnProperty('active') ? true : false;
    if (value) {
      listEl.removeAttribute('style');
    }
    else {
      const initHeight = listEl.offsetHeight;
      listEl.style.height = 'auto';
      listEl.style.transition = 'unset';
      const height = listEl.offsetHeight;
      listEl.style.height = `${initHeight}px`;
      listEl.style.transition = null;
      setTimeout(() => {
        listEl.style.height = `${height}px`;
      });
    }
    booleanDataset(this.refs.toggle.parentElement, 'active', !value);
  }
}

customElements.define('archive-feed', ArchiveFeed);
